import { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CONFIG, TOP_TIER, BOTTOM_TIER, promoteCountForGroup, relegateCountForGroup, tierName } from '@h2h/shared';
import { AppShell, Content } from '../components/ui/AppShell';
import { DesktopColumns } from '../components/ui/DesktopColumns';
import { Placeholder } from '../components/ui/Placeholder';
import { ChevronLeft, ArrowRight, Trophy } from '../components/ui/icons';
import { useHaptics } from '../hooks/useHaptics';
import {
  useLeagues,
  useCurrentRound,
  useMyEntry,
  useRoundFixtures,
  useMyGroup,
  useMyMembership
} from '../hooks/useQueries';
import { useAuth } from '../context/AuthContext';
import { identityFor } from '../data/leagues';
import { RoundResultsHero, TierLadder } from '../components/rounds/RoundResultsHero';
import { GroupStandingsBoard } from '../components/standings/StandingsRow';
import { DivisionsHelp } from '../components/standings/DivisionsHelpSheet';
import { PickScoreRow } from '../components/rounds/PickScoreRow';
import { DuelScoreRow } from '../components/rounds/DuelRows';
import { buildCardVMs } from '../lib/round';
import { buildDuelVMs } from '../lib/cyclingRound';
import { cap, signedPoints } from '../lib/format';

export default function RoundResults() {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const { leagueId } = useParams<{ leagueId: string }>();
  const [params] = useSearchParams();
  const { uid } = useAuth();
  const { data: leagues } = useLeagues();
  const league = leagues?.find((l) => l.leagueId === leagueId);
  const identity = identityFor(leagueId ?? '');
  const name = league?.name ?? identity?.name ?? 'League';
  const isCycling = league?.sport === 'CYCLING';
  const roundId = params.get('round') ?? league?.currentRoundId ?? null;

  const { data: round } = useCurrentRound(league?.leagueId, roundId);
  const { data: entry, isLoading: entryLoading } = useMyEntry(league?.leagueId, roundId, uid);
  const { data: fixtures } = useRoundFixtures(league?.leagueId, roundId);
  const { data: group } = useMyGroup(league?.leagueId, roundId, uid);
  const { data: membership } = useMyMembership(league?.leagueId, uid);

  const fixtureMap = useMemo(
    () => new Map((fixtures ?? []).map((f) => [f.fixtureId, f])),
    [fixtures]
  );
  const picks = useMemo(
    () => (entry && !isCycling ? buildCardVMs(entry, fixtureMap) : []),
    [entry, fixtureMap, isCycling]
  );
  const duels = useMemo(
    () => (entry && isCycling ? buildDuelVMs(entry, fixtureMap) : []),
    [entry, fixtureMap, isCycling]
  );

  const settled = !!entry && entry.movement != null && entry.roundScore != null;
  const roundOpen = round?.status === 'OPEN';
  const roundLive =
    round?.status === 'LOCKING' || round?.status === 'SETTLING' || round?.status === 'OPEN';
  const movement = entry?.movement ?? 'STAYED';
  const fromTier = entry?.tier ?? membership?.tier ?? 0;
  const toTier = membership?.tier ?? fromTier;
  const tierDelta = toTier - fromTier;
  const divisionSize = group?.memberIds.length ?? CONFIG.DIVISION_SIZE;

  const won = (entry?.rankInGroup ?? 0) === 1;
  const outcomeBadge =
    movement === 'PROMOTED'
      ? `TOP ${promoteCountForGroup(divisionSize)} · PROMOTION ZONE`
      : movement === 'RELEGATED'
        ? `BOTTOM ${relegateCountForGroup(divisionSize)} · RELEGATION`
        : won
          ? 'GROUP WINNER'
          : 'HELD · SAFE';

  return (
    <AppShell>
      <Content width="wide">
        <div className="flex items-center justify-between gap-3 pt-2 md:pt-5">
          <button
            type="button"
            onClick={() => {
              impact('light');
              navigate(leagueId ? `/play/${leagueId}` : '/football');
            }}
            className="tap -ml-1 flex min-w-0 shrink items-center gap-[2px] text-ink"
          >
            <ChevronLeft width={22} height={22} strokeWidth={2.4} className="shrink-0" />
            <span className="truncate text-[16px] font-semibold tracking-snug">{name}</span>
          </button>
          <div className="eyebrow min-w-0 flex-1 truncate text-right font-bold">{round?.label ?? name} · Final</div>
        </div>

        {!settled ? (
          <>
            <Placeholder
              Icon={Trophy}
              title={
                entryLoading
                  ? isCycling
                    ? 'Loading your stage…'
                    : 'Loading your round…'
                  : isCycling
                    ? 'This stage hasn’t settled yet'
                    : 'This round hasn’t settled yet'
              }
              body={
                isCycling
                  ? 'Once the stage finishes and the finishing order is final, your duel scores and tier move appear here.'
                  : 'Once every fixture finishes and the round settles, your result and tier move appear here.'
              }
            />
            <div className="mt-4 flex flex-col gap-2">
              {entry && roundLive && (
                <button
                  type="button"
                  onClick={() => {
                    impact('medium');
                    navigate(
                      isCycling
                        ? `/play/${leagueId}/standings${roundId ? `?round=${roundId}` : ''}`
                        : `/play/${leagueId}/waiting${roundId ? `?round=${roundId}` : ''}`
                    );
                  }}
                  className="tap flex w-full items-center justify-center gap-2 rounded-[18px] bg-night py-[15px] shadow-dark"
                >
                  <span className="text-[15px] font-extrabold text-white">
                    {isCycling ? 'Live stage board' : 'Live card board'}
                  </span>
                  <ArrowRight width={16} height={16} strokeWidth={2.4} className="text-white" />
                </button>
              )}
              {!entry && roundOpen && (
                <button
                  type="button"
                  onClick={() => {
                    impact('medium');
                    navigate(`/play/${leagueId}${roundId ? `?round=${roundId}` : ''}`);
                  }}
                  className="tap flex w-full items-center justify-center gap-2 rounded-[18px] bg-[linear-gradient(140deg,#2EDB7B,#0BA078)] py-[15px] shadow-[0_16px_30px_-16px_rgba(11,160,120,0.55)]"
                >
                  <span className="text-[15px] font-extrabold text-white">
                    {isCycling ? 'Call your duels' : 'Build your cards'}
                  </span>
                  <ArrowRight width={16} height={16} strokeWidth={2.4} className="text-white" />
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  impact('light');
                  navigate(`/play/${leagueId}`);
                }}
                className="tap flex w-full items-center justify-center rounded-[18px] border border-black/5 bg-card py-[14px] shadow-tile"
              >
                <span className="text-[14px] font-bold text-ink">Back to Compete</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="pt-3">
              <RoundResultsHero
                outcome={movement}
                outcomeBadge={outcomeBadge}
                rank={entry!.rankInGroup ?? 0}
                divisionSize={divisionSize}
                fromTierIndex={fromTier}
                toTierIndex={toTier}
                won={won}
              />
            </div>

            <div className="pt-[18px]">
              <div className="flex items-center justify-between rounded-[22px] border border-black/5 bg-card p-[16px_18px] shadow-tile">
                <div className="text-[11px] font-bold uppercase tracking-wide text-ink2">
                  {round?.label ?? 'Round'} score
                </div>
                <span className="num text-[38px] font-extrabold leading-none tracking-title text-ink">
                  {signedPoints(entry!.roundScore ?? 0)}
                </span>
              </div>
            </div>

            <DesktopColumns
              asideWidth="360px"
              main={
                <>
                  <div className="flex items-center justify-between pt-[22px] md:pt-0">
                    <span className="text-[13px] font-bold tracking-snug text-ink">
                      {isCycling ? 'How your duels scored' : 'How your cards scored'}
                    </span>
                    <span className="text-[11px] font-semibold text-ink2">{isCycling ? 'base × stake' : 'base × boosts'}</span>
                  </div>

                  <div className="flex flex-col gap-[10px] pt-3">
                    {isCycling
                      ? duels.map((vm) => <DuelScoreRow key={vm.duelId} vm={vm} />)
                      : picks.map((pick) => <PickScoreRow key={pick.fixtureId} pick={pick} />)}
                  </div>
                </>
              }
              aside={
                <>
                  {uid && group && group.standings.length > 0 && (
                    <div className="pt-[24px] md:pt-0">
                      <GroupStandingsBoard
                        standings={group.standings}
                        uid={uid}
                        size={Math.max(group.memberIds.length, group.standings.length)}
                        promoteTarget={fromTier >= TOP_TIER ? undefined : cap(tierName(fromTier + 1))}
                        relegateTarget={fromTier <= BOTTOM_TIER ? undefined : cap(tierName(fromTier - 1))}
                      />
                    </div>
                  )}

                  <div className="pt-[22px]">
                    <TierLadder currentTierIndex={toTier} tiersGained={tierDelta} />
                    <div className="mt-3 flex justify-center">
                      <DivisionsHelp tierIndex={toTier} variant="inline" label="How divisions work" />
                    </div>
                  </div>
                </>
              }
            />

            <div className="pt-5">
              <button
                type="button"
                onClick={() => {
                  impact('medium');
                  navigate(`/play/${leagueId}`);
                }}
                className="tap flex w-full items-center justify-center gap-[9px] rounded-[18px] bg-night py-[17px] shadow-dark"
              >
                <span className="text-[16px] font-extrabold tracking-snug text-white">Back to {name}</span>
                <ArrowRight width={17} height={17} strokeWidth={2.4} className="text-white" />
              </button>
              <p className="mt-3 text-center text-[11px] font-semibold text-ink2">
                {movement === 'STAYED' ? (
                  won ? (
                    <>You topped your group in <span className="font-bold text-ink">{cap(tierName(toTier))}</span> — round won</>
                  ) : (
                    <>You held your place in <span className="font-bold text-ink">{cap(tierName(toTier))}</span></>
                  )
                ) : (
                  <>
                    <span className="font-bold text-accent-ink">New division</span> · a fresh group of up to{' '}
                    {CONFIG.DIVISION_SIZE} rivals awaits in {cap(tierName(toTier))}
                  </>
                )}
              </p>
            </div>
          </>
        )}
      </Content>
    </AppShell>
  );
}
